import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  LinkedIn,
  Instagram,
  Facebook,
} from "@mui/icons-material";

const SocialProfilesModal = ({ open, onClose, onSave, initialData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [socialLinks, setSocialLinks] = useState({
    linkedin: "",
    instagram: "",
    facebook: "",
  });
  const [errors, setErrors] = useState({
    linkedin: "",
    instagram: "",
    facebook: "",
  });

  // Validation functions
  const validateLinkedIn = (url) => {
    if (!url) return "";
    
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/(in|pub|company)\/[a-zA-Z0-9\-\.]+\/?$/;
    
    if (!linkedinRegex.test(url)) {
      return "Invalid LinkedIn URL. Must be linkedin.com/in/username or linkedin.com/company/companyname";
    }
    
    return "";
  };

  const validateInstagram = (url) => {
    if (!url) return "";
    
    const instagramRegex = /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9\._]+\/?$/;
    
    if (!instagramRegex.test(url)) {
      return "Invalid Instagram URL. Must be instagram.com/username";
    }
    
    return "";
  };

  const validateFacebook = (url) => {
    if (!url) return "";
    
    const facebookRegex = /^https?:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9\.]+\/?$/;
    
    if (!facebookRegex.test(url)) {
      return "Invalid Facebook URL. Must be facebook.com/username or facebook.com/pages/username";
    }
    
    return "";
  };

  useEffect(() => {
    if (open && initialData) {
      setSocialLinks({
        linkedin: initialData.linkedin || "",
        instagram: initialData.instagram || "",
        facebook: initialData.facebook || "",
      });
    }
  }, [open, initialData]);

  const handleInputChange = (platform, value) => {
    setSocialLinks(prev => ({
      ...prev,
      [platform]: value
    }));

    // Validate specific platforms
    let errorMessage = "";
    if (platform === "linkedin") {
      errorMessage = validateLinkedIn(value);
    } else if (platform === "instagram") {
      errorMessage = validateInstagram(value);
    } else if (platform === "facebook") {
      errorMessage = validateFacebook(value);
    }

    setErrors(prev => ({
      ...prev,
      [platform]: errorMessage
    }));
  };

  // Check if at least one field has a valid value
  const hasValidField = () => {
    const linkedinError = validateLinkedIn(socialLinks.linkedin);
    const instagramError = validateInstagram(socialLinks.instagram);
    const facebookError = validateFacebook(socialLinks.facebook);

    // Check if at least one field has a value and is valid (no error)
    const hasValidLinkedIn = socialLinks.linkedin && !linkedinError;
    const hasValidInstagram = socialLinks.instagram && !instagramError;
    const hasValidFacebook = socialLinks.facebook && !facebookError;

    return hasValidLinkedIn || hasValidInstagram || hasValidFacebook;
  };

  const handleSave = () => {
    // Validate all fields before saving
    const linkedinError = validateLinkedIn(socialLinks.linkedin);
    const instagramError = validateInstagram(socialLinks.instagram);
    const facebookError = validateFacebook(socialLinks.facebook);

    setErrors({
      linkedin: linkedinError,
      instagram: instagramError,
      facebook: facebookError,
    });

    // Check if there are any validation errors
    if (linkedinError || instagramError || facebookError) {
      return;
    }

    onSave && onSave(socialLinks);
    onClose();
  };

  const handleClose = () => {
    setSocialLinks({
      linkedin: "",
      instagram: "",
      facebook: "",
    });
    setErrors({
      linkedin: "",
      instagram: "",
      facebook: "",
    });
    onClose();
  };

  const socialPlatforms = [
    {
      key: "linkedin",
      name: "LinkedIn",
      icon: <LinkedIn sx={{ color: "#0077B5", fontSize: 24 }} />,
      placeholder: "https://linkedin.com/in/yourprofile",
    },
    {
      key: "instagram",
      name: "Instagram",
      icon: (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: 1,
            background: "linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Instagram sx={{ color: "white", fontSize: 16 }} />
        </Box>
      ),
      placeholder: "https://instagram.com/yourprofile",
    },
    {
      key: "facebook",
      name: "Facebook",
      icon: (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#1877F2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Facebook sx={{ color: "white", fontSize: 16 }} />
        </Box>
      ),
      placeholder: "https://facebook.com/yourprofile",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: { xs: 0, md: 2 } }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        pb: 1
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '17px', md: '20px' }, letterSpacing: 0 }}>
          Connect your social profiles
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, px: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, md: 3 } }}>
          {socialPlatforms.map((platform) => (
            <Box key={platform.key}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
                {platform.name}
              </Typography>
              <TextField
                fullWidth
                placeholder={platform.placeholder}
                value={socialLinks[platform.key]}
                onChange={(e) => handleInputChange(platform.key, e.target.value)}
                error={!!errors[platform.key]}
                helperText={errors[platform.key]}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {platform.icon}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, md: 3 }, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasValidField()}
          sx={{
            textTransform: 'none',
            background: 'linear-gradient(45deg, #4D6BF6 0%, #7C3AED 100%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #3B5BDB 0%, #6D28D9 100%)',
            },
            '&:disabled': {
              background: '#E5E7EB',
              color: '#9CA3AF',
            }
          }}
        >
          Connect profiles
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SocialProfilesModal;
